import React, { useState, useEffect, useMemo } from 'react';
import Text, { Inline } from 'components/base/Text';
import { Flex, Padding } from 'components/base/layout-components';
import Icon from 'components/base/Icon';
import { resolveIterationsPickerTooltipByPath } from 'connected-components/TopBar/TopBar.service';
import ItemPicker from 'components/ItemPicker';
import Tooltip from '@material-ui/core/Tooltip';
import { TRACKING } from 'common/constants/constants';
import { useDispatch, useSelector } from 'react-redux';
import { selectedTeamProject as selectedTeamProjectSelector } from 'state/selectors/projects';
import { CircularProgress } from '@material-ui/core';
import _ from 'lodash';
import {
  PulseIterationPickerLoadIterationsOverlay,
  PulseIterationPickerLoadingOverlay
} from './PulseIterationsPickerOverlay';
import { DateSelectionChanged, updateProject } from 'state/callers';
import { FEATURE_FLAGS_NAMES, SettingValues } from '@linearb/linjs-common';
import {
  selectedTeamSelector,
  uiStateSelector
} from 'state/selectors/ui-state';
import { METHOD_NAMES } from 'modals/CreateEditProjectModal/consts';
import { boardsProviderKeysSelector } from 'state/selectors/boards';
import * as TrackingService from 'common/services/tracking.service';
import { isSelectedTeamParentSelector } from 'state/selectors/teams';
import useSettingsService from 'components/hooks/useSettingsService';
import useCloseIterationSummary from 'components/IterationSummary/useCloseIterationSummary';
import {
  getLatestIterationSelector,
  iterationsPickerSelector
} from 'state/selectors/iterations-picker';
import { useHistory } from 'react-router';
import { getCurrentIteration } from 'common/utils/utils';

const LoadingTooltipTitle = (
  <Padding t='x-small' b='x-small'>
    <Text color='shuttleGray' size='medium'>
      Generating data for <strong>past iterations</strong>.<br /> Please check
      back later.
    </Text>
  </Padding>
);

const getLoader = (loadingPDTProject, tooltipPlacement = 'bottom') =>
  loadingPDTProject && (
    <Tooltip title={LoadingTooltipTitle} placement={tooltipPlacement}>
      <CircularProgress size={20} style={{ padding: '0.2rem' }} />
    </Tooltip>
  );

const normalizeIteration = ({ uiState, controlledIteration, isControlled }) =>
  isControlled
    ? {
        dates: {
          from: controlledIteration.start_date,
          to: controlledIteration.end_date
        },
        selectedIterationId: controlledIteration.id
      }
    : {
        dates: { from: uiState.dates.from, to: uiState.dates.to },
        selectedIterationId: uiState.selectedIterationId
      };

/* controlledIterationId, onChange are conditional props for controlled component */
const PulseIterationPicker = ({
  loadingPDTProject: isLoading,
  controlledIterationId,
  onChange,
  iterationStates = [],
  menuStyle = {}
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const iterationsPickerData = useSelector(iterationsPickerSelector);
  const uiState = useSelector(uiStateSelector);
  const controlledIteration = useMemo(
    () =>
      iterationsPickerData
        .filter(iteration => Boolean(iteration.id))
        .find(iteration => iteration.id === controlledIterationId),
    [iterationsPickerData, controlledIterationId]
  );
  const isControlled = !!controlledIteration && !!onChange;

  const {
    dates: { from, to },
    selectedIterationId
  } = normalizeIteration({ uiState, controlledIteration, isControlled });

  const latestIteration = useSelector(getLatestIterationSelector);

  const { getSettingOrDefinitionValue } = useSettingsService();
  const [iterationName, setIterationName] = useState('');
  const [iterationRange, setIterationRange] = useState('');
  const isFFOn = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.teamPMConfigurationEnabled
  });
  const isLinearbOnly = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.isLinearbOnly
  });
  const { onCloseIterationSummary } = useCloseIterationSummary();

  const iterationsPickerTooltip = resolveIterationsPickerTooltipByPath(
    history.location.pathname
  );
  const [loadingPDTProject, setLoadingPDTProject] = useState(false);
  const selectedTeamProject = useSelector(selectedTeamProjectSelector);
  const selectedTeam = useSelector(selectedTeamSelector);
  const boards = useSelector(boardsProviderKeysSelector);

  const isGroup = useSelector(isSelectedTeamParentSelector);
  const isPickerEnabled = (isFFOn && selectedTeam) || isLinearbOnly;
  const showDisabledDatePicker = !isPickerEnabled || isGroup;
  const pdtProjectId = _.get(selectedTeamProject, 'pdt_project_id');
  const today = new Date();
  const sixMonthsAgo = new Date(today).setMonth(today.getMonth() - 6);

  useEffect(() => {
    setLoadingPDTProject(isLoading);
  }, [isLoading]);

  const getProjectPayload = () => {
    const iterationsStrategySetting = getSettingOrDefinitionValue({
      name: SettingValues.ITERATION_STRATEGY,
      teamId: _.get(selectedTeam, 'id')
    });
    const cadence = _.get(iterationsStrategySetting, 'customIterations')
      ? METHOD_NAMES.KANBAN
      : METHOD_NAMES.SCRUM;
    const payload = _.omit(selectedTeamProject, ['id']);
    const filterBoards = _.get(selectedTeamProject, 'filter.boards', []);
    const filterBoardsWithNames = filterBoards.map(({ provider_id }) => ({
      provider_id,
      name: _.get(boards, `${provider_id}.name`)
    }));
    const filter = {
      ..._.get(selectedTeamProject, 'filter', {}),
      boards: filterBoardsWithNames
    };
    return {
      ...payload,
      filter,
      metadata: { cadence }
    };
  };
  const onLoadPreviousIterations = ({ setAnchor }) => {
    dispatch(updateProject(selectedTeamProject?.id, getProjectPayload()));
    setLoadingPDTProject(true);
    setAnchor(null);
    TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
      page: TRACKING.PAGES.PULSE,
      click_type: TRACKING.CLICK.NEW_PULSE_LOAD_PREVIOUS_ITERATIONS
    });
  };

  useEffect(() => {
    const currentIteration = getCurrentIteration(
      iterationsPickerData,
      from,
      to,
      selectedIterationId
    );
    if (currentIteration) {
      setIterationName(currentIteration.name);
      setIterationRange(currentIteration.range);
    } else {
      setIterationName(latestIteration?.name);
      setIterationRange(latestIteration?.range);
    }
  }, [iterationsPickerData, from, to]);

  const isIterationDisabled = ({ id }) =>
    loadingPDTProject && selectedIterationId !== id;

  const getIterationMenuItem = ({ computedName, name }) => (
    <Text
      lineHeight='2.5rem'
      size='large'
      color='shark'
      ellipsisAfter
      data-cy='iteration-picker-item'
    >
      {computedName || name}
    </Text>
  );

  const getMenuOverlay = childProps => {
    if (!Boolean(pdtProjectId) && !loadingPDTProject) {
      return (
        <PulseIterationPickerLoadIterationsOverlay
          onLoadIterations={() => onLoadPreviousIterations(childProps)}
        />
      );
    }
    if (loadingPDTProject) {
      return <PulseIterationPickerLoadingOverlay />;
    }
    return null;
  };

  const onIterationSelected = iteration => {
    onCloseIterationSummary();

    if (isControlled) {
      onChange(iteration);
    } else {
      dispatch(DateSelectionChanged(iteration));
    }

    TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
      page: TRACKING.PAGES.PULSE,
      click_type: TRACKING.CLICK.NEW_PULSE_ITERATION_SELECTION,
      iteration_name: _.get(iteration, 'name'),
      iteration_start_date: _.get(iteration, 'start_date'),
      iteration_end_date: _.get(iteration, 'end_date')
    });
  };
  return (
    <ItemPicker
      minWidth='16.5rem'
      dataAttrName='iteration'
      disabled={showDisabledDatePicker}
      getOverlayItem={getMenuOverlay}
      isSelectedFunc={iteration => selectedIterationId === iteration.id}
      menuItemsDisabled={loadingPDTProject || !Boolean(pdtProjectId)}
      itemMapper={iteration => {
        return (
          <Flex justify='space-between' items='center' width='100%'>
            {getIterationMenuItem(iteration)}
          </Flex>
        );
      }}
      items={iterationsPickerData
        .filter(
          ({ isVisible, id, end_date }) =>
            new Date(end_date) > sixMonthsAgo && isVisible && Boolean(id)
        )
        .filter(
          iteration =>
            !iterationStates?.length ||
            iterationStates.includes(iteration.state)
        )
        .map(iteration => ({
          ...iteration,
          disabled: isIterationDisabled(iteration)
        }))}
      onItemChange={onIterationSelected}
      menuStyle={menuStyle}
    >
      <Tooltip
        title={iterationsPickerTooltip || ''}
        disableHoverListener={!showDisabledDatePicker}
      >
        <Flex
          items='center'
          direction='row'
          width='90%'
          justify='space-between'
        >
          <Flex items='center' direction='row'>
            <Icon name='calendaricon' size='medium' color='nepal' />
            <Flex direction='column' pl='small'>
              <Inline
                size='large'
                weight={400}
                color='shark'
                ellipsisAfter={showDisabledDatePicker ? '14rem' : '12rem'}
              >
                {iterationName}
              </Inline>
              <Inline
                size='medium'
                weight={400}
                color='shuttleGray'
                ellipsisAfter={showDisabledDatePicker ? '14rem' : '12rem'}
              >
                {iterationRange}
              </Inline>
            </Flex>
          </Flex>

          {getLoader(loadingPDTProject)}
        </Flex>
      </Tooltip>
    </ItemPicker>
  );
};

export default PulseIterationPicker;
